import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Check } from "lucide-react";
import { useState } from "react";
import { ErrorState } from "@/components/brand/error-state";
import { Eyebrow } from "@/components/brand/eyebrow";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type EditorDestination,
  HeadlineSection,
  PillarsSection,
  PolicySection,
  PROMPTS_KEY,
  type SectionKey,
  SectionNav,
  StructureSection,
  TipSheetSection,
  useBrandConfig,
  useUpdateTemplate,
} from "@/features/brand-templates";
import { PromptsSection } from "@/features/prompts";
import type { BrandConfigDetail } from "@/lib/api";

export const Route = createFileRoute("/_authenticated/admin/templates/$templateId")({
  component: TemplateEditorPage,
});

function TemplateEditorPage() {
  const { templateId } = Route.useParams();
  const { data: template, isPending, isError, refetch } = useBrandConfig(templateId);
  const [active, setActive] = useState<EditorDestination>("voicePillars");

  if (isPending) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-20 rounded-lg" />
        <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
          <Skeleton className="h-80 rounded-lg" />
          <Skeleton className="h-96 rounded-lg" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        title="Could not load this template"
        description="It may have been deleted, or the request failed."
        onRetry={() => void refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <EditorHeader template={template} />
      <div className="grid items-start gap-6 lg:grid-cols-[260px_1fr]">
        <div className="lg:sticky lg:top-6">
          <SectionNav progress={template.progress} active={active} onSelect={setActive} />
        </div>
        <div>
          {active === PROMPTS_KEY ? (
            <PromptsSection configId={template.id} pillars={template.contentPillars} />
          ) : (
            renderSection(active, template)
          )}
        </div>
      </div>
    </div>
  );
}

function renderSection(key: SectionKey, template: BrandConfigDetail) {
  switch (key) {
    case "voicePillars":
      return <PillarsSection template={template} kind="voice" />;
    case "contentPillars":
      return <PillarsSection template={template} kind="content" />;
    case "headlineFormula":
      return <HeadlineSection template={template} />;
    case "postStructure":
      return <StructureSection template={template} />;
    case "tipSheet":
      return <TipSheetSection template={template} />;
    case "socialPolicy":
      return <PolicySection template={template} />;
  }
}

function EditorHeader({ template }: { template: BrandConfigDetail }) {
  const save = useUpdateTemplate(template.id);
  const published = template.status === "published";
  // Publishing is what makes a template offerable during org creation, so the
  // API requires both pillar sets. Mirror that here rather than let it 422.
  const canPublish = template.progress.voicePillars && template.progress.contentPillars;

  return (
    <div className="space-y-4">
      <Button asChild variant="ghost" size="sm" className="-ml-2">
        <Link to="/admin/templates">
          <ArrowLeft /> All templates
        </Link>
      </Button>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <Eyebrow>{template.industry ?? "Brand template"}</Eyebrow>
          <h1 className="mt-1 font-display text-4xl text-destructive md:text-5xl">
            {template.name}
          </h1>
          {template.description ? (
            <p className="mt-2 max-w-2xl text-muted-foreground text-sm">{template.description}</p>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {published ? (
            <Badge variant="success">
              <Check /> Ready to use
            </Badge>
          ) : (
            <Badge variant="outline">Draft</Badge>
          )}
          <Button
            variant={published ? "outline" : "default"}
            disabled={(!published && !canPublish) || save.isPending}
            onClick={() =>
              save.mutate({
                path: { config_id: template.id },
                body: { status: published ? "draft" : "published" },
              })
            }
          >
            {published ? "Move back to draft" : "Make available"}
          </Button>
        </div>
      </div>

      {!published && !canPublish ? (
        <p className="rounded-md border border-border bg-muted/40 px-4 py-3 text-muted-foreground text-sm">
          Add at least one voice pillar and one content pillar to make this template available when
          creating an organisation.
        </p>
      ) : null}
    </div>
  );
}
